import { defineStore } from 'pinia'
import { api, TOKEN_KEY, type LoginResult, type SecondStep } from '@/api/client'
import type { CaptchaAnswer } from '@/utils/captcha'
import type { ExpLevel } from '@/utils/lvBadge'

// Only role=user accounts can ever authenticate against the customer
// engine (see service.Auth's allowedRoles in control/internal/service) —
// staff (admin/operator) sign in through the separate admin console.
export interface User {
  id: number
  username: string
  email: string
  role: 'user'
  status: string
  /** 实名认证: unverified, pending, verified or rejected. */
  realname_status?: string
  /** individual or enterprise. */
  realname_type?: string
  phone?: string
  created_at?: string
  /** 安全中心: when the MFA device was bound; absent while none is. */
  mfa_enabled_at?: string
  /** Absent for a password set before 安全中心 recorded changes. */
  password_changed_at?: string
  /** The phone was proved with a texted code. */
  phone_verified_at?: string
  /** 登录保护 by text message. */
  login_sms?: boolean
  /** 账户资料: the nickname and the avatar's address, when there are. */
  nickname?: string
  avatar_url?: string
  /** The 子用户 signed in to this account, when one is (not the account itself). */
  sub_user?: { id: number; username: string; remark: string; last_login_at?: string }
  /** How many 通行密钥 the account has; absent for a sub-user. */
  passkeys?: number
  /** VIP 等级, as the platform grants it in 客户列表 (0: not a VIP). */
  vip_level?: number
  /** 经验, and the 等级 it reaches (会员等级). */
  exp?: number
  exp_level?: ExpLevel
}

interface State {
  token: string | null
  user: User | null
}

export const useAuthStore = defineStore('auth', {
  state: (): State => ({
    token: localStorage.getItem(TOKEN_KEY),
    user: null,
  }),
  getters: {
    isLoggedIn: (s) => !!s.token,
  },
  actions: {
    // Signs in, or — for an account with a second step (an MFA device,
    // 登录保护 by text) — returns what loginMFA needs: the ticket, the ways
    // to finish, the masked number a text goes to.
    async login(username: string, password: string, remember = false, captcha?: CaptchaAnswer): Promise<LoginResult | null> {
      const res = await api.login(username, password, remember, captcha)
      if (res.mfa_required) return res
      this.signIn(res)
      return null
    },
    // 验证码登录: as login, with a code sent to target instead of the password.
    async loginByCode(target: string, code: string, remember = false): Promise<LoginResult | null> {
      const res = await api.loginByCode(target, code, remember)
      if (res.mfa_required) return res
      this.signIn(res)
      return null
    },
    async loginMFA(ticket: string, code: string, method: SecondStep = 'mfa', remember = false) {
      this.signIn(await api.loginMFA(ticket, code, method, remember))
    },
    // 子用户: signed in to the main account, as the sub-user — /me says which.
    async loginSub(owner: string, username: string, password: string, remember = false, captcha?: CaptchaAnswer) {
      this.signIn(await api.loginSub(owner, username, password, remember, captcha))
      await this.refreshMe()
    },
    // 通行密钥: the browser's answer to the ceremony; no second step follows.
    async loginPasskey(ceremony: string, credential: Record<string, any>, remember = false) {
      this.signIn(await api.loginPasskey(ceremony, credential, remember))
    },
    signIn(res: LoginResult) {
      this.user = res.user
      this.useToken(res.access_token ?? '')
    },
    // A new token for this session: 修改密码 and 退出其他设备 end the old one.
    useToken(token: string) {
      this.token = token
      localStorage.setItem(TOKEN_KEY, token)
    },
    async refreshMe() {
      if (!this.token) return
      try {
        this.user = await api.me()
      } catch (e) {
        // Signed out only when the server says the session is over; a
        // network failure or a server error keeps it, for a reload to try
        // again — a slow link must not cost the sign-in.
        const status = (e as { response?: { status?: number } }).response?.status
        if (status === 401 || status === 403) this.logout()
      }
    },
    logout() {
      this.token = null
      this.user = null
      localStorage.removeItem(TOKEN_KEY)
      // Re-direct happens in the router guard.
    },
  },
})
